/**
 * Нягтлан бодогчийн тооцоо (2026-10-15) — сервер өгөгдлөө уншаад энд
 * бодуулна, дэлгэц ба Excel ИЖИЛ тоо харуулна. Мөнгө бүгд `Decimal`.
 *
 * Шийдвэрүүд (эзэмшигч, 2026-10-15):
 *  · Буцаалт БУЦСАН ӨДӨРТ нь хасна — өнгөрсөн өдрийн тоо хэзээ ч
 *    өөрчлөгдөхгүй, кассын хаалттай таарна.
 *  · Ханш = ЦУГЛУУЛАГЧИЙН оруулсан ханш (аяллын «Солиулсан ханш»).
 *    Монголбанкны ханш оролцохгүй (CLAUDE.md дүрэм 4).
 */
import { Decimal } from 'decimal.js';
const ZERO = new Decimal(0);
/**
 * Өдөр × салбар × нийлүүлэгчээр нэгтгэнэ. Эрэмбэ: өдөр, салбар, нийлүүлэгч (нэрээр).
 * Солилцоо = хуучин мөрийн буцаалт + шинэ мөрийн борлуулалт — цэвэр нь зөв гарна.
 */
export function dailySalesMatrix(events) {
    const map = new Map();
    for (const e of events) {
        const key = `${e.date}|${e.storeId}|${e.supplierId}`;
        let row = map.get(key);
        if (!row) {
            row = {
                date: e.date, storeId: e.storeId, storeName: e.storeName,
                supplierId: e.supplierId, supplierName: e.supplierName,
                soldUnits: 0, returnedUnits: 0, salesMnt: ZERO, returnsMnt: ZERO, netMnt: ZERO,
            };
            map.set(key, row);
        }
        const amount = new Decimal(e.amountMnt);
        if (e.kind === 'sale') {
            row.soldUnits += 1;
            row.salesMnt = row.salesMnt.plus(amount);
        }
        else {
            row.returnedUnits += 1;
            row.returnsMnt = row.returnsMnt.plus(amount);
        }
        row.netMnt = row.salesMnt.minus(row.returnsMnt);
    }
    const rows = [...map.values()].sort((a, b) => a.date.localeCompare(b.date) ||
        a.storeName.localeCompare(b.storeName) ||
        a.supplierName.localeCompare(b.supplierName));
    const totals = rows.reduce((t, r) => ({
        soldUnits: t.soldUnits + r.soldUnits,
        returnedUnits: t.returnedUnits + r.returnedUnits,
        salesMnt: t.salesMnt.plus(r.salesMnt),
        returnsMnt: t.returnsMnt.plus(r.returnsMnt),
        netMnt: t.netMnt.plus(r.netMnt),
    }), { soldUnits: 0, returnedUnits: 0, salesMnt: ZERO, returnsMnt: ZERO, netMnt: ZERO });
    return { rows, totals };
}
/**
 * Огнооны мужтай дэвтэр. `from`-оос өмнөх бүх гүйлгээ эхний үлдэгдэлд
 * ордог; `[from, to)` доторх мөр бүрт үлдэгдэл. Ижил агшинд зээл төлбөрөөс
 * ӨМНӨ (тэр өдөр авсаныг тэр өдөр төлж болно — үлдэгдэл сөрөг гарахгүй).
 */
export function supplierLedger(events, from, to) {
    const sorted = [...events].sort((a, b) => a.at.getTime() - b.at.getTime() || (a.kind === b.kind ? 0 : a.kind === 'credit' ? -1 : 1));
    let opening = ZERO;
    let balance = ZERO;
    let taken = ZERO;
    let paid = ZERO;
    const lines = [];
    for (const e of sorted) {
        if (e.at >= to)
            continue;
        const amount = new Decimal(e.amountRub);
        const signed = e.kind === 'credit' ? amount : amount.negated();
        if (e.at < from) {
            opening = opening.plus(signed);
            balance = opening;
            continue;
        }
        balance = balance.plus(signed);
        if (e.kind === 'credit')
            taken = taken.plus(amount);
        else
            paid = paid.plus(amount);
        lines.push({
            at: e.at,
            kind: e.kind,
            ref: e.ref,
            note: e.note ?? null,
            debitRub: e.kind === 'credit' ? amount : ZERO,
            creditRub: e.kind === 'payment' ? amount : ZERO,
            balanceRub: balance,
            amountMnt: e.rate === null ? null : amount.times(e.rate).toDecimalPlaces(2),
            paidMnt: e.paidMnt === null || e.paidMnt === undefined ? null : new Decimal(e.paidMnt),
        });
    }
    return { openingRub: opening, lines, takenRub: taken, paidRub: paid, closingRub: opening.plus(taken).minus(paid) };
}
// ---------------------------------------------------------------- 4. Ханшийн тэгшитгэл
/**
 * Аяллын ханш = цуглуулагчийн оруулсан ₮ / ₽ (аппын «Солиулсан ханш»).
 * ₽ тэг эсвэл ₮ тэг бол ханшгүй — тооцоонд оруулахгүй.
 */
export function tripRate(declaredTotalMnt, declaredTotalRub) {
    const mnt = new Decimal(declaredTotalMnt);
    const rub = new Decimal(declaredTotalRub);
    if (rub.lessThanOrEqualTo(0) || mnt.lessThanOrEqualTo(0))
        return null;
    return mnt.div(rub);
}
/**
 * Нэг төлбөрийн ханшийн зөрүү. ₽100,000 өрийг 38.00 ханшаар бүртгэсэн
 * (3,800,000₮), 3,900,000₮-ээр төлсөн → гарз 100,000₮.
 */
export function fxDifference(amountRub, rate, paidMnt) {
    const rub = new Decimal(amountRub);
    const booked = rub.times(rate).toDecimalPlaces(2);
    const paid = new Decimal(paidMnt);
    return {
        bookedMnt: booked,
        paidRate: rub.isZero() ? ZERO : paid.div(rub).toDecimalPlaces(4),
        diffMnt: paid.minus(booked),
    };
}
//# sourceMappingURL=accounting.js.map